import { Component, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import {
    BatteryService,
    BatterySetDto,
    BatterySetService,
    BatteryTypeDto,
    BatteryTypeService,
    FEATURE_ORGANISATION_RELATIONSHIPS,
    PersonsOrganisationDto,
    PreferencesService,
    SimpleOrganisationDto,
    WorkTracker,
    compareDates,
    hasAnyPermission,
    hasFeatureFlag,
    toLookup
} from '@flyfreely-portal-ui/flyfreely';
import {
    ColumnSortPreferences,
    TableColumn,
    TableConfig,
    TableSearchValues,
    TableSetupUserPreferences
} from '@flyfreely-portal-ui/flyfreely-table';
import { FormatResourceStatusPipe } from '@flyfreely-portal-ui/resource-ui';
import { FormatDatePipe } from '@flyfreely-portal-ui/ui';
import { Widget, WidgetRegistration } from '@flyfreely-portal-ui/widget';
import { WorkspaceStateService } from '@flyfreely-portal-ui/workspace';
import { BatteryDialoguesService } from 'libs/batteries/src/lib/battery-dialogues.service';
import { findDefaultColumnSelection } from 'libs/flyfreely-table/src/lib/helpers';
import { dateRange } from 'libs/flyfreely-table/src/lib/search';
import * as moment from 'moment';
import { EMPTY, Observable, Subject, combineLatest, of } from 'rxjs';
import { catchError, map, switchMap, takeUntil } from 'rxjs/operators';
import { WidgetsPreferencesService } from '../widget-preferences.service';

const BATTERY_SET_IDENTIFIER = 'batterySetList';

interface BatterySetWithType extends BatterySetDto {
    batteryType?: BatteryTypeDto;
    convertedAssemblyDate: moment.Moment;
    convertedDisassemblyDate: moment.Moment;
}

@Component({
    selector: 'battery-set-list-widget',
    templateUrl: './battery-set-list-widget.component.html',
    styles: [
        `
            :host {
                scroll-margin-top: 60px;
            }
        `
    ]
})
export class BatterySetListWidgetComponent
    implements OnInit, OnDestroy, Widget
{
    organisation?: PersonsOrganisationDto;

    availableColumns?: TableColumn[];
    selectedColumns?: string[];
    tableConfig: TableConfig;
    columnSorting?: ColumnSortPreferences;
    tableSearch: TableSearchValues;

    batterySets: BatterySetWithType[];

    canAddBatterySet: boolean;
    userPreferences: TableSetupUserPreferences;

    hasCustodian = false;

    public working: boolean;
    private workTracker = new WorkTracker();

    private ngUnsubscribe$ = new Subject<void>();

    private showDummyData$: Observable<boolean>;
    private batterySets$ = new Subject<BatterySetWithType[]>();
    private batteryTypes$ = new Subject<BatteryTypeDto[]>();

    constructor(
        private batteryTypeService: BatteryTypeService,
        private batterySetService: BatterySetService,
        private batteryService: BatteryService,
        private batteryDialogues: BatteryDialoguesService,
        private workspaceStateService: WorkspaceStateService,
        private preferencesService: PreferencesService,
        private formatDatePipe: FormatDatePipe,
        private resourceStatusPipe: FormatResourceStatusPipe,
        private preferences: WidgetsPreferencesService
    ) {
        this.showDummyData$ = preferencesService.showDummyData$.pipe(
            takeUntil(this.ngUnsubscribe$)
        );

        this.selectedColumns = null;

        this.columnSorting = null;

        this.tableSearch = null;
    }

    ngOnInit() {
        this.workspaceStateService.currentLoadedOrganisation$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(organisation => {
                this.organisation = organisation.organisation;
                this.refreshPermissions();
                this.refreshBatteryTypes();
                this.refreshBatterySets();
                this.setupColumns();
                this.loadPreferences();
            });
        this.workTracker.observable
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(working => (this.working = working));

        combineLatest([
            this.batterySets$,
            this.showDummyData$,
            this.batteryTypes$
        ])
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(([batterySets, showDummy, batteryTypes]) => {
                const batteryTypeLookup = batteryTypes.reduce(toLookup, {});

                this.batterySets = batterySets
                    .filter(m => showDummy || !m.isDummy)
                    .map(s => ({
                        ...s,
                        batteryType:
                            s.batteries.length === 0
                                ? null
                                : batteryTypeLookup[
                                      s.batteries[0].batteryTypeId
                                  ]
                    }));
            });

        this.batterySetService.change$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(() => {
                this.refreshBatterySets();
            });
    }

    ngOnDestroy() {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    ngOnChanges(changes: SimpleChanges) {
        // check for changes to the bindings
        if ('organisation' in changes) {
            this.refreshPermissions();
            this.refreshBatteryTypes();
            this.refreshBatterySets();
            this.loadPreferences();
        }
    }

    private refreshPermissions() {
        this.canAddBatterySet = hasAnyPermission(
            this.organisation,
            PersonsOrganisationDto.Permissions.BATTERYSET_ADD
        );
        this.hasCustodian = hasFeatureFlag(
            this.organisation,
            FEATURE_ORGANISATION_RELATIONSHIPS
        );
    }

    private setupColumns() {
        this.availableColumns = [
            {
                value: 'name',
                name: 'Name',
                searchable: true,
                selectable: false,
                defaultSelection: true
            },
            {
                value: 'convertedAssemblyDate',
                key: 'assemblyDate',
                name: 'Assembly Date',
                searchable: 'daterange',
                selectable: true,
                defaultSelection: true,
                formatterFunction: d => this.formatDatePipe.transform(d),
                searchFunction: dateRange,
                compareFunction: (a, b) => compareDates(a.rawData, b.rawData)
            },
            {
                value: 'convertedDisassemblyDate',
                key: 'disassemblyDate',
                name: 'Disassembly Date',
                searchable: 'daterange',
                selectable: true,
                defaultSelection: false,
                formatterFunction: d => this.formatDatePipe.transform(d),
                searchFunction: dateRange,
                compareFunction: (a, b) => compareDates(a.rawData, b.rawData)
            },
            {
                value: 'batteryType.make',
                name: 'Battery Type Make',
                searchable: true,
                selectable: true,
                defaultSelection: true
            },
            {
                value: 'batteryType.model',
                name: 'Battery Type Model',
                searchable: true,
                selectable: true,
                defaultSelection: true
            },
            {
                value: 'status',
                name: 'Status',
                searchable: 'selection',
                selectable: true,
                defaultSelection: true,
                searchOptions: this.batteryService.getStatuses(),
                defaultSearch: 'SERVICEABLE',
                formatterFunction: s => this.resourceStatusPipe.transform(s)
            },
            {
                value: 'organisation.name',
                name: 'Owner',
                searchable: true,
                selectable: true,
                defaultSelection: false
            },
            {
                value: 'maintenanceOrganisation.name',
                name: 'Maintenance Organisation',
                searchable: true,
                selectable: true,
                defaultSelection: false
            },
            ...(this.hasCustodian
                ? [
                      {
                          value: 'custodian',
                          name: 'Custodian',
                          searchable: true,
                          selectable: true,
                          defaultSelection: true,
                          formatterFunction: (a: SimpleOrganisationDto) =>
                              a == null ? 'Owner' : a.name,
                          searchFunction: (searchValue: string) => {
                              if (searchValue == null || searchValue === '') {
                                  return () => true;
                              }
                              const normalised = searchValue
                                  .trim()
                                  .toUpperCase();
                              return (
                                  custodian: SimpleOrganisationDto,
                                  row: BatterySetWithType
                              ) => {
                                  const name =
                                      custodian == null
                                          ? `Owner ${row.organisation.name}`
                                          : custodian.name;
                                  return (
                                      name
                                          .trim()
                                          .toUpperCase()
                                          .indexOf(normalised) !== -1
                                  );
                              };
                          }
                      } as TableColumn
                  ]
                : [])
        ];
    }

    private loadPreferences() {
        if (!this.organisation) {
            return;
        }

        this.tableConfig = {
            timeZone: this.organisation.timeZone,
            limit: 10,
            actions: [],
            limitSelection: [10, 25, 50, 100]
        };

        this.preferences
            .findPreferences('batterySetListWidgetTable', this.organisation.id)
            .pipe(
                switchMap(preferences => {
                    if (preferences == null) {
                        return this.preferences
                            .findLegacyPreferences(
                                'batterySetListWidgetColumns',
                                this.organisation.id
                            )
                            .pipe(
                                map(pref => ({
                                    selectedColumns: pref,
                                    columnSorting: null,
                                    tableSearch: null,
                                    itemLimit: this.tableConfig.limit
                                })),
                                catchError(() => {
                                    this.handleDefaultPreferences();
                                    return of(null);
                                })
                            );
                    } else {
                        return of(preferences);
                    }
                }),
                catchError(() => {
                    this.selectedColumns = findDefaultColumnSelection(
                        this.availableColumns
                    );
                    return EMPTY;
                })
            )
            .subscribe(preferences => {
                this.applyPreferences(preferences);
            })
            .add(this.workTracker.createTracker());
    }

    private handleDefaultPreferences() {
        this.applyPreferences({});
    }

    private applyPreferences(preferences: TableSetupUserPreferences) {
        this.userPreferences = this.preferences.applyPreferences(
            preferences,
            this.availableColumns,
            this.tableConfig
        );

        this.selectedColumns = this.userPreferences.selectedColumns;
        this.columnSorting = this.userPreferences.columnSorting;
        this.tableSearch = this.userPreferences.tableSearch;
        this.tableConfig.limit = this.userPreferences.itemLimit;

        this.preferences.updateDateRange(this.tableSearch, 'assemblyDate');
        this.preferences.updateDateRange(this.tableSearch, 'disassemblyDate');
    }

    private refreshBatteryTypes() {
        const doneWorking = this.workTracker.createTracker();
        this.batteryTypeService
            .find(this.organisation.id)
            .toPromise()
            .then(batteryTypes => {
                this.batteryTypes$.next(batteryTypes);
                doneWorking();
            });
    }

    private refreshBatterySets() {
        this.batterySets = null;
        if (!this.organisation) {
            return;
        }
        const doneWorking = this.workTracker.createTracker();
        this.batterySetService
            .findBatterySets(this.organisation.id)
            .toPromise()
            .then(results => {
                this.batterySets$.next(
                    results.map(r => ({
                        ...r,
                        convertedAssemblyDate: r.assemblyDate
                            ? moment(r.assemblyDate)
                            : null,
                        convertedDisassemblyDate: r.disassemblyDate
                            ? moment(r.disassemblyDate)
                            : null
                    }))
                );
                doneWorking();
            });
    }

    newBatterySet() {
        this.batteryDialogues.showAddBatterySetDialogue(this.organisation.id);
    }

    showBatterySet(batterySet: BatterySetDto) {
        this.batteryDialogues.showBatterySetDetailsDialogue(
            batterySet,
            this.organisation
        );
    }

    updateSelectedColumns(selectedColumns: string[]) {
        this.selectedColumns = selectedColumns;
        if (
            this.columnSorting != null &&
            !this.selectedColumns.includes(this.columnSorting.column)
        ) {
            this.columnSorting = null;
        }
        this.userPreferences = {
            ...this.userPreferences,
            selectedColumns: selectedColumns,
            columnSorting: this.columnSorting || null
        };
        this.updateUserPreferences();
    }

    updateColumnSorting(sorting: ColumnSortPreferences) {
        this.columnSorting = sorting;
        this.userPreferences = {
            ...this.userPreferences,
            columnSorting: sorting
        };
        this.updateUserPreferences();
    }

    updateSearchPreferences(search: any) {
        this.tableSearch = search;
        this.userPreferences = {
            ...this.userPreferences,
            tableSearch: search
        };
        this.updateUserPreferences();
    }

    updateItemLimit(limit: number) {
        this.tableConfig.limit = limit;
        this.userPreferences = {
            ...this.userPreferences,
            itemLimit: limit
        };
        this.updateUserPreferences();
    }

    updateUserPreferences() {
        this.preferencesService
            .updatePreferences(
                'batterySetListWidgetTable',
                this.organisation.id,
                this.userPreferences
            )
            .subscribe();
    }
}

export const batterySetWidgetRegistration: WidgetRegistration = {
    widgetIdentifier: BATTERY_SET_IDENTIFIER,
    component: BatterySetListWidgetComponent,
    isAvailable: organisation =>
        hasAnyPermission(
            organisation,
            PersonsOrganisationDto.Permissions.BATTERYSET_LIST
        )
};
